import { UnlistUnitTiming } from "api/landlord/templates/types";
import { Checkbox, Input, InputSize, Radio } from "shared/components";
import { cx } from "shared/utils";
import {
  BellRinging01,
  Building06,
  Calender,
  Code01,
  Eye,
  HomeLine
} from "untitledui-js-base";
import { useSettingsController } from "views/settings-developers/controller";
import { useTemplateContext } from "../provider";
import { SettingCard } from "../components/SettingCard";
import { InfoTooltip } from "../components/InfoTooltip";
import { CheckComponent } from "../components/CheckComponent";
import { SwitchWithReadOnlyMode } from "../SwitchWithReadOnlyMode";

export const GeneralSettingsView = () => {
  const { template, setTemplate, readOnly } = useTemplateContext();
  const {
    automatically_unlist_unit_timing,
    notify_applicants,
    has_pet_rent,
    pet_rent_value,
    has_voucher,
    display_rent_amount,
    display_available_date,
    add_applicants_to_pms_on_approve,
    add_applicants_to_pms_on_conditionally_approve,
    add_applicants_to_pms_on_decline,
    has_move_in_date_limit,
    max_move_in_days
  } = template || {};
  const { isAppfolioEverySyncEnabled, isRentersWarehouse, isEverySyncEnabled } =
    useSettingsController();

  return (
    <div className="space-y-6 w-full max-w-[844px] pb-6">
      <SettingCard
        title="Listings"
        Icon={Building06}
        disabled={isEverySyncEnabled}
      >
        <h3 className="text-base font-medium text-content-1000 mb-4 flex gap-2 items-center">
          Automatically disallow applying for unit
          {isAppfolioEverySyncEnabled && (
            <InfoTooltip
              text={`Ability to submit applications is always synced with AppFolio.
                       You can change the sync settings on the "Settings" > "Developers"`}
            />
          )}
          {isRentersWarehouse && (
            <InfoTooltip text="Ability to submit applications is always synced with Renters Warehouse" />
          )}
        </h3>

        <div>
          {readOnly ? (
            automatically_unlist_unit_timing ===
            UnlistUnitTiming.ON_FIRST_APPLICATION ? (
              <CheckComponent
                checked={true}
                label="When you receive the first completed application"
                labelClassName="!text-content-400"
              />
            ) : null
          ) : (
            <Radio
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    automatically_unlist_unit_timing:
                      UnlistUnitTiming.ON_FIRST_APPLICATION
                  });
              }}
              className="!gap-0 mb-2"
              labelClassName="!text-content-400"
              id="unist-on-first-application"
              name="unlist"
              label="When you receive the first completed application"
              checked={
                automatically_unlist_unit_timing ===
                UnlistUnitTiming.ON_FIRST_APPLICATION
              }
              disabled={isAppfolioEverySyncEnabled || readOnly}
            />
          )}

          {readOnly ? (
            automatically_unlist_unit_timing === UnlistUnitTiming.ON_APPROVE ? (
              <CheckComponent
                checked={true}
                label="When you approve or conditionally approve an application"
                labelClassName="!text-content-400"
              />
            ) : null
          ) : (
            <Radio
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    automatically_unlist_unit_timing:
                      UnlistUnitTiming.ON_APPROVE
                  });
              }}
              className="!gap-0 mb-2"
              labelClassName="!text-content-400"
              id="unlist-on-approve"
              name="unlist"
              label="When you approve or conditionally approve an application"
              checked={
                automatically_unlist_unit_timing === UnlistUnitTiming.ON_APPROVE
              }
              disabled={isAppfolioEverySyncEnabled || readOnly}
            />
          )}
          {readOnly ? (
            automatically_unlist_unit_timing === UnlistUnitTiming.NEVER ? (
              <CheckComponent
                checked={true}
                label="Never"
                labelClassName="!text-content-400"
              />
            ) : null
          ) : (
            <Radio
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    automatically_unlist_unit_timing: UnlistUnitTiming.NEVER
                  });
              }}
              className="!gap-0 mb-2"
              labelClassName="!text-content-400"
              id="unist-never"
              name="unlist"
              label="Never"
              checked={
                automatically_unlist_unit_timing === UnlistUnitTiming.NEVER
              }
              disabled={isAppfolioEverySyncEnabled || readOnly}
            />
          )}
        </div>
      </SettingCard>
      <SettingCard title="Visibility" Icon={Eye}>
        <div className="space-y-2">
          <SwitchWithReadOnlyMode
            readOnly={readOnly}
            label={
              <p
                className={!display_rent_amount && readOnly ? "opacity-50" : ""}
              >
                Display the rent amount for applicants
              </p>
            }
            checked={Boolean(display_rent_amount)}
            onChange={() => {
              template &&
                setTemplate({
                  ...template,
                  display_rent_amount: !Boolean(display_rent_amount)
                });
            }}
          />
          <SwitchWithReadOnlyMode
            readOnly={readOnly}
            label={
              <p
                className={
                  readOnly && !display_available_date ? "opacity-50" : ""
                }
              >
                Display the available by date for applicants
              </p>
            }
            checked={Boolean(display_available_date)}
            onChange={() => {
              template &&
                setTemplate({
                  ...template,
                  display_available_date: !display_available_date
                });
            }}
          />
        </div>
      </SettingCard>

      <SettingCard title="Move-in date" Icon={Calender}>
        <div className={cx("flex items-center")}>
          <SwitchWithReadOnlyMode
            readOnly={readOnly}
            label={
              <p
                className={cx(
                  readOnly ? "!text-content-400" : "",
                  readOnly && !has_move_in_date_limit ? "opacity-50" : ""
                )}
              >
                Set requested move-in date limit for applicants
              </p>
            }
            checked={Boolean(has_move_in_date_limit)}
            onChange={() => {
              template &&
                setTemplate({
                  ...template,
                  has_move_in_date_limit: !Boolean(has_move_in_date_limit),
                  max_move_in_days: template.max_move_in_days ?? 20
                });
            }}
          />

          <div
            className={cx(
              "flex flex-row items-center gap-4 ml-auto",
              readOnly && !has_move_in_date_limit ? "opacity-50" : ""
            )}
          >
            <Input
              className="w-full max-w-20"
              label={
                <span
                  className={cx(
                    !has_move_in_date_limit
                      ? "text-content-100"
                      : "text-content-1000",
                    "font-medium"
                  )}
                >
                  Max
                </span>
              }
              hideOptional
              id="date"
              type="number"
              noIntlNormalization
              size={InputSize.SM}
              value={max_move_in_days}
              placeholder="20"
              readOnly={!Boolean(has_move_in_date_limit) || readOnly}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                template &&
                  setTemplate({
                    ...template,
                    max_move_in_days: value
                  });
              }}
            />
            <span
              className={cx(
                !has_move_in_date_limit
                  ? "text-content-100"
                  : "text-content-1000",
                "text-sm font-medium mb-1"
              )}
            >
              days from submission
            </span>
          </div>
        </div>
      </SettingCard>

      <SettingCard title="Notifications" Icon={BellRinging01}>
        <SwitchWithReadOnlyMode
          readOnly={readOnly}
          label={
            <p className={readOnly && !notify_applicants ? "opacity-50" : ""}>
              Notify applicants about a decision via email
            </p>
          }
          checked={Boolean(notify_applicants)}
          onChange={() => {
            template &&
              setTemplate({
                ...template,
                notify_applicants: !Boolean(notify_applicants)
              });
          }}
        />
      </SettingCard>

      <SettingCard
        title="Application monthly rent"
        Icon={HomeLine}
        tooltip="Group income-to-rent and debt-to-income ratio calculations use “Application monthly rent”. Application monthly rent matches your listing rent amount by default. Use the settings below to update the application monthly rent based on group information"
      >
        <div className="space-y-2">
          <div className="flex items-center">
            <SwitchWithReadOnlyMode
              readOnly={readOnly}
              label={
                <p className={readOnly && !has_pet_rent ? "opacity-50" : ""}>
                  Add pet rent to the listed rent amount (excluding service
                  animals)
                </p>
              }
              checked={Boolean(has_pet_rent)}
              onChange={() => {
                if (Boolean(has_pet_rent))
                  template &&
                    setTemplate({
                      ...template,
                      has_pet_rent: !Boolean(has_pet_rent),
                      pet_rent_value: undefined
                    });
                else
                  template &&
                    setTemplate({
                      ...template,
                      has_pet_rent: !Boolean(has_pet_rent)
                    });
              }}
            />

            <div
              className={cx(
                "flex flex-row-reverse items-center gap-4 ml-auto",
                !has_pet_rent ? "opacity-50" : ""
              )}
            >
              <Input
                className="ml-auto w-full max-w-20"
                label={
                  <span className="font-medium text-content-1000">per pet</span>
                }
                hideOptional
                id="fee"
                type="money"
                size={InputSize.SM}
                value={pet_rent_value?.cents ? pet_rent_value.cents / 100 : 0}
                placeholder="60"
                readOnly={!Boolean(has_pet_rent) || readOnly}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) * 100;
                  template &&
                    setTemplate({
                      ...template,
                      pet_rent_value: { cents: value, currency: "USD" }
                    });
                }}
              />
            </div>
          </div>
          <SwitchWithReadOnlyMode
            readOnly={readOnly}
            label={
              <p className={readOnly && !has_voucher ? "opacity-50" : ""}>
                Deduct housing assistance (e.g., Section 8 vouchers) from the
                listed rent amount
              </p>
            }
            checked={Boolean(has_voucher)}
            onChange={() => {
              template &&
                setTemplate({
                  ...template,
                  has_voucher: !Boolean(has_voucher)
                });
            }}
          />
        </div>
      </SettingCard>

      <SettingCard title="Integration" Icon={Code01}>
        <h3 className="text-base font-medium text-content-1000 mb-4">
          Add applicants to Property Management Software
        </h3>

        <div>
          {readOnly ? (
            <CheckComponent
              checked={Boolean(add_applicants_to_pms_on_approve)}
              label="When you approve an application"
              labelClassName={
                !add_applicants_to_pms_on_approve ? "opacity-50" : ""
              }
              wrapperClassName="mb-2"
            />
          ) : (
            <Checkbox
              checked={Boolean(add_applicants_to_pms_on_approve)}
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    add_applicants_to_pms_on_approve: !Boolean(
                      add_applicants_to_pms_on_approve
                    )
                  });
              }}
              disabled={readOnly}
              label="When you approve an application"
              wrapperClassName="mb-2"
            />
          )}

          {readOnly ? (
            <CheckComponent
              checked={Boolean(add_applicants_to_pms_on_conditionally_approve)}
              label="When you conditionally approve an application"
              labelClassName={
                !add_applicants_to_pms_on_conditionally_approve
                  ? "opacity-50"
                  : ""
              }
              wrapperClassName="mb-2"
            />
          ) : (
            <Checkbox
              checked={Boolean(add_applicants_to_pms_on_conditionally_approve)}
              disabled={readOnly}
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    add_applicants_to_pms_on_conditionally_approve: !Boolean(
                      add_applicants_to_pms_on_conditionally_approve
                    )
                  });
              }}
              label="When you conditionally approve an application"
              wrapperClassName="mb-2"
            />
          )}

          {readOnly ? (
            <CheckComponent
              checked={Boolean(add_applicants_to_pms_on_decline)}
              label="When you decline an application"
              labelClassName={
                !add_applicants_to_pms_on_decline ? "opacity-50" : ""
              }
              wrapperClassName="mb-2"
            />
          ) : (
            <Checkbox
              checked={Boolean(add_applicants_to_pms_on_decline)}
              disabled={readOnly}
              onChange={() => {
                template &&
                  setTemplate({
                    ...template,
                    add_applicants_to_pms_on_decline: !Boolean(
                      add_applicants_to_pms_on_decline
                    )
                  });
              }}
              label="When you decline an application"
              wrapperClassName="mb-2"
            />
          )}
        </div>
      </SettingCard>
    </div>
  );
};
